import React, { useContext, useRef, useState } from 'react'
import clsx from 'clsx'
import { useMutation } from '@apollo/react-hooks'
import _get from 'lodash/get'

import { makeStyles } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import CardContent from '@material-ui/core/CardContent'
import CardActions from '@material-ui/core/CardActions'
import Grid from '@material-ui/core/Grid'
import CircularProgress from '@material-ui/core/CircularProgress'

import { useAuth0 } from '@/auth/index'
import { ProjectItemWidgetContext } from '@/widgets/project-item-widget'
import PagAutocomplete from '@/components/pag-form/pag-autocomplete'
import { safeLabel } from '@/utils/option-label'
import { errorHandler } from '@/shared/error-handler'
import { Context } from '@/shared/store/reducers/global-reducer'
import { callPagApi } from '@/shared/services/pag-api'
import PagTextInput from '@/components/pag-form/pag-text-input'
import { IProjectAssetType } from '@/shared/models/project-asset-type'
import { ADD_PROJECT_ASSET, UPDATE_PROJECT_ASSET } from '@/shared/graphql/mutations/mutationProjectAsset'


const useStyles = makeStyles((theme) => ({
  root: {
    position: 'absolute',
    top: -184,
    right: 8,
    minWidth: 280,
    maxWidth: 280,
    boxShadow:
      '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
    zIndex: 1001,
    overflow: 'visible',
  },
  form: {
    display: 'block',
  },
  cardHeader: {
    paddingTop: 8,
    paddingBottom: 0,
  },
  cardContent: {
    position: 'relative',
    padding: theme.spacing(2),
    paddingBottom: 0,
  },
  cardAction: {
    padding: theme.spacing(2),
    justifyContent: 'flex-end',
  },
  button: {
    minWidth: 100,
  },
  circularProgress: {
    position: 'absolute',
  },
}))

export const ProjectAssetEditComponent = (props: any) => {
  const {
    projectAsset,
    projectAssets,
    project,
    handleChange,
    handleCancel,
  } = props
  const classes = useStyles()
  const { getTokenSilently } = useAuth0()
  const { store } = useContext(Context)
  const [projectAssetTypeId, setProjectAssetTypeId] = useState<number>(projectAsset?.projectAssetTypeId ?? 0);
  const [assetCount, setAssetCount] = useState<number>(projectAsset?.assetCount ?? 0);
  const { projectAssetTypes } = useContext(ProjectItemWidgetContext)
  const { activeTenant } = store
  const rootEleRef = useRef(null)

  const [addProjectAsset, addProjectAssetRes] = useMutation(
    ADD_PROJECT_ASSET,
    {
      onError: (err) => {
        errorHandler(err)
      },
      onCompleted: (res) => {
        const temp = _get(res, ['addProjectAsset'])
        if (temp) {
          handleChange([...projectAssets, temp], false)
        }
      },
    }
  )

  const [updateProjectAsset, updateProjectAssetRes] = useMutation(
    UPDATE_PROJECT_ASSET,
    {
      onError: (err) => {
        errorHandler(err)
      },
      onCompleted: (res) => {
        const temp = _get(res, ['updateProjectAsset'])
        if (temp) {
          handleChange([...projectAssets.filter((item: any) => item.id !== temp.id), temp], false)
        }
      }
    },
    
  )

  const handleFormSubmit = () => {
    let abortController: any
    try {
      abortController = new AbortController()

      if (projectAsset?.id) {
        callPagApi('', getTokenSilently, updateProjectAsset, {
          tenantId: activeTenant?.id,
          id: projectAsset.id,
          projectId: project.id,
          projectAssetTypeId,
          assetCount,
        }, abortController.signal)
      } else {
        callPagApi('', getTokenSilently, addProjectAsset, {
          tenantId: activeTenant?.id,
          projectId: project.id,
          projectAssetTypeId,
          assetCount,
        }, abortController.signal)
      }
    } catch (err) {
      console.error('Error in Add/Edit Project Asset', err)
      if (abortController) abortController.abort()
    }
  }

  return (
    <Card ref={rootEleRef} className={clsx(classes.root)} elevation={24} variant="outlined">
      <CardHeader
        className={classes.cardHeader}
        title={ !!projectAsset ? "Edit Project Asset" : "Add new Project Asset"}
        titleTypographyProps={{
          variant: 'h6',
        }}
      />
      <CardContent className={classes.cardContent}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <PagAutocomplete
              {...{
                name: 'projectAssetId',
                optionLabelKey: 'name',
                optionValueKey: 'id',
                options: projectAssetTypes,
                value: projectAssetTypeId,
                isCreatable: false,
                AutocompleteProps: {
                  getOptionLabel: (option: IProjectAssetType) => safeLabel(option?.name),
                  size: 'small',
                  disableClearable: true,
                  onChange: (
                    event: object,
                    values: IProjectAssetType,
                    reason: string
                  ) => {
                      setProjectAssetTypeId(values ? values.id : 0)
                  },
                },
                InputProps: {
                  variant: 'outlined',
                  label: 'Asset Type',
                  placeholder: 'Select a Asset Type',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>

          <PagTextInput
            label="Asset Count"
            placeholder="Enter Asset Count"
            size="small"
            variant="outlined"
            InputProps={{
              type: "number",
              name: 'length',
              inputProps: {
                thousandSeparator: true,
                isNumericString: true,
                decimalScale: 0,
              },
            }}
            // error={fieldState.error}
            value={assetCount.toString()}
            onChange={(e: any) => {
              setAssetCount(Number(e.target.value))
            }}
          />
          </Grid>
        </Grid>
      </CardContent>
      <CardActions className={classes.cardAction}>
        <Button
          color="primary"
          size="medium"
          aria-label="cancel"
          variant="text"
          className={classes.button}
          disabled={addProjectAssetRes.loading || updateProjectAssetRes.loading}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        &nbsp;&nbsp;&nbsp;
        <Button
          color="primary"
          size="medium"
          aria-label="next"
          variant="contained"
          className={classes.button}
          disabled={addProjectAssetRes.loading || updateProjectAssetRes.loading}
          onClick={() => handleFormSubmit()}
        >
          { !!projectAsset ? "Edit" : "Add"}
          {(addProjectAssetRes.loading || updateProjectAssetRes.loading) && (
            <CircularProgress size={16} color="primary" className={classes.circularProgress} />
          )}
        </Button>
      </CardActions>
    </Card>
  )
}

ProjectAssetEditComponent.defaultProps = {
  handleChange: (e: any, shouldCallApi: boolean) => {},
  handleCancel: () => {},
}

export default ProjectAssetEditComponent